<template>
  <v-row justify="center">
    <v-dialog v-model="open" width="532px" :fullscreen="isMobileBreakpoint">
      <v-card dense>
        <v-toolbar class="pa-7" elevation="0">
          <v-toolbar-title
            class="body-regular-medium font-weight-medium"
            :class="{
              'neutral-20-dark--text': !$vuetify.theme.dark,
            }"
            style="line-height: 120%"
          >
            {{ $t(modalTexts.title) }}
            <p
              v-if="!isMultiple"
              class="text-caption mb-0"
              style="white-space: normal"
              :class="{
                'neutral-40-light--text': !$vuetify.theme.dark,
              }"
            >
              {{
                $t(modalTexts.subtitle, {
                  groupName: group.name,
                  platform: platformLabel(group),
                  count: countVideos,
                })
              }}
            </p>
          </v-toolbar-title>
          <v-icon
            @click="handleClose"
            class="close-icon ml-auto"
            size="24"
            color="grey"
            style="cursor: pointer"
          >
            mdi-close
          </v-icon>
        </v-toolbar>
        <v-card-text class="px-7 pb-0 pt-2">
          <v-row no-gutters>
            <v-col cols="12" class="mb-8">
              <BaseAlert
                type="warning"
                :title="$t(modalTexts.adviceTitle)"
                :description="$t(modalTexts.adviceSubscription)"
              />
            </v-col>
            <v-col cols="12" class="input">
              <v-input
                v-model="password"
                :label="$t(modalTexts.inputPasswordLabel)"
                :placeholder="$t(modalTexts.inputPasswordPlaceholder)"
                :type="showPassword ? 'text' : 'password'"
                :disabled="isFetching"
                autocomplete="off"
                :error="passwordError"
                :error-messages="passwordErrorMessages"
                @keyup.enter="handleAction"
                @focus="resetState"
              >
                <template #append>
                  <v-icon
                    @click="showPassword = !showPassword"
                    style="cursor: pointer"
                    class="neutral-60--text"
                    size="16"
                  >
                    {{
                      showPassword ? 'panda-eye-hidden' : 'panda-eye-appearing'
                    }}
                  </v-icon>
                </template>
              </v-input>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions
          class="pa-7 card-actions"
          :class="{ 'mobile-actions': isMobileBreakpoint }"
        >
          <div v-if="!isMobileBreakpoint" class="d-flex align-center">
            <a
              type="secondary"
              size="xsmall"
              class="brand-60-base--text"
              style="font-size: 14px"
              @click="openDrmInfo"
            >
              {{ $t(modalTexts.infoLink) }}
              <v-icon
                size="14px"
                class="page__progressBar--article-icon brand-60-base--text"
              >
                mdi-open-in-new
              </v-icon>
            </a>
          </div>
          <v-spacer></v-spacer>
          <base-btn
            type="tertiary"
            class="mr-3"
            text
            @click="handleClose"
            :disabled="isFetching"
          >
            {{ $t('message.cancel') }}
          </base-btn>
          <base-btn
            :disabled="!canAction || isFetching"
            :loading="isFetching"
            @click="handleAction"
          >
            <span>{{ $t(modalTexts.action) }}</span>
          </base-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import BaseAlert from '@/components/BaseAlert.vue'
import drmApi from '@/services/drmApi'
import { mapActions, mapMutations, mapState } from 'vuex'

export default {
  name: 'DrmActivationGroup',
  components: {
    BaseAlert,
  },
  data: () => ({
    open: false,
    isFetching: false,
    countVideos: 0,
    groups: [],
    group: {
      name: '',
      id: '',
      active: false,
    },
    password: '',
    showPassword: false,
    passwordError: false,
    passwordErrorMessage: '',
    apiError: null,
  }),
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
      isTabletBreakpoint: 'isTabletBreakpoint',
    }),
    isActivating() {
      const groups = this.isMultiple ? this.groups : [this.group]
      return groups.every((g) => !g.active)
    },
    isMultiple() {
      return !!this.groups?.length
    },
    canAction() {
      return this.password.length > 0
    },
    passwordErrorMessages() {
      return this.passwordError
        ? [this.$t(this.modalTexts.inputPasswordInvalid)]
        : []
    },
    modalTexts() {
      return this.isActivating
        ? {
            title: 'message.pages.drm.modal.activateGroup.title',
            subtitle: 'message.pages.drm.modal.activateGroup.subtitle',
            adviceTitle: 'message.pages.drm.modal.activateGroup.adviceTitle',
            adviceSubscription:
              'message.pages.drm.modal.activateGroup.adviceSubscription',
            inputPasswordLabel:
              'message.pages.drm.modal.activateGroup.inputPasswordLabel',
            inputPasswordPlaceholder:
              'message.pages.drm.modal.activateGroup.inputPasswordPlaceholder',
            inputPasswordInvalid:
              'message.pages.drm.modal.activateGroup.inputPasswordInvalid',
            infoLink: 'message.pages.drm.modal.activateGroup.infoLink',
            action: 'message.pages.drm.modal.activateGroup.activate',
          }
        : {
            title: 'message.pages.drm.modal.deactivateGroup.title',
            subtitle: 'message.pages.drm.modal.deactivateGroup.subtitle',
            adviceTitle: 'message.pages.drm.modal.deactivateGroup.adviceTitle',
            adviceSubscription:
              'message.pages.drm.modal.deactivateGroup.adviceSubscription',
            inputPasswordLabel:
              'message.pages.drm.modal.deactivateGroup.inputPasswordLabel',
            inputPasswordPlaceholder:
              'message.pages.drm.modal.deactivateGroup.inputPasswordPlaceholder',
            inputPasswordInvalid:
              'message.pages.drm.modal.deactivateGroup.inputPasswordInvalid',
            infoLink: 'message.pages.drm.modal.deactivateGroup.infoLink',
            action: 'message.pages.drm.modal.deactivateGroup.deactivate',
          }
    },
    platformLabel() {
      return (group) => {
        const source = group.webhook_source_name || 'api'
        const platformLabels = {
          hotmart: 'Hotmart',
          eduzz: 'Eduzz',
          memberkit: 'Memberkit',
          'the-members': 'The Members',
          ticto: 'Ticto',
          cademi: 'Cademi',
          xgrow: 'Xgrow',
          api: 'API',
        }
        return platformLabels[source]
      }
    },
  },
  mounted() {
    this.$root.$on(
      'drmActivateGroupModal',
      ({ group, groups, countVideos }) => {
        this.group = { ...group }
        this.groups = groups
        this.countVideos = countVideos
        this.resetState()
        this.hideCrispButton()
        this.open = true
      }
    )
  },
  beforeDestroy() {
    this.$root.$off('drmActivateGroupModal')
    this.showCrispButton()
  },
  methods: {
    ...mapActions('session', ['signInUser']),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    openDrmInfo() {
      const LINK_MAP = {
        pt: 'https://help.pandavideo.com/pt-br/article/o-que-e-e-como-funciona-o-nosso-drm-watermark-1tnesei/',
        en: 'https://help.pandavideo.com/en-us/article/what-is-drm-watermark-and-how-does-it-work-1tirjk3/',
        es: 'https://help.pandavideo.com/es/article/que-es-y-como-funciona-nuestro-drm-marca-de-agua-12eaco2/',
      }
      window.open(LINK_MAP[this.$i18next.language], '_blank')
    },
    resetState() {
      this.passwordError = false
      this.passwordErrorMessage = ''
      this.password = ''
      this.showPassword = false
      this.showCrispButton()
    },
    async validatePassword() {
      this.passwordError = false
      this.passwordErrorMessage = ''
      try {
        await this.signInUser({
          username: this.$store.getters['session/userAttributes'].email,
          password: this.password,
        })
        return true
      } catch (error) {
        this.passwordError = true
        this.passwordErrorMessage = this.$t(
          this.modalTexts.inputPasswordInvalid
        )
        return false
      }
    },
    async handleAction() {
      this.isFetching = true

      const isPasswordValid = await this.validatePassword()
      if (!isPasswordValid) {
        this.isFetching = false
        return
      }

      this.$root.$emit('closeDrmAdvancedModal')

      const groupsToUpdate = this.isMultiple ? this.groups : [this.group]
      const targetStatus = this.isActivating

      try {
        await Promise.all(
          groupsToUpdate.map((g) =>
            drmApi.drmUpdateVideos(g.id, { active: targetStatus })
          )
        )

        let messageKey = 'message.pages.drm.modal.'

        if (targetStatus) {
          messageKey += this.isMultiple
            ? 'activateGroup.successActivateMultiple'
            : 'activateGroup.successActivate'
        } else {
          messageKey += this.isMultiple
            ? 'deactivateGroup.successDeactivateMultiple'
            : 'deactivateGroup.successDeactivate'
        }

        const message = this.$t(messageKey)

        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: message,
          type: 'success',
        })
      } catch (e) {
        console.log('e', e)
      }

      this.$root.$emit(
        'updateDrmGroup',
        groupsToUpdate.map((g) => ({ ...g, active: targetStatus }))
      )
      this.$root.$emit('updateDrmGroup', { active: targetStatus })
      this.handleClose()
      this.isFetching = false
    },
    handleClose() {
      this.open = false
      this.resetState()
    },
    hideCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton && this.isMobileBreakpoint) {
        crispButton.style.setProperty('display', 'none', 'important')
      }
    },
    showCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton) {
        crispButton.style.removeProperty('display')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-toolbar {
  height: 90px !important;
}

::v-deep .v-toolbar__content {
  padding: 0 !important;
  align-items: flex-start !important;
}

::v-deep .v-input__append-inner {
  margin: auto !important;
}

.input::v-deep {
  label {
    left: -10px !important;
  }
  input::placeholder {
    font-size: 12px !important;
  }
}
.card-actions {
  border-top: 1px solid var(--v-neutral-95-lightest-base) !important;
}
.mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  .base-button {
    width: 50%;
  }
}
.drm-info-link {
  color: #1976d2;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
}
</style>
